import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConstantsContext = createContext(null);

export const useConstants = () => {
  const context = useContext(ConstantsContext);
  if (!context) {
    throw new Error('useConstants must be used within ConstantsProvider');
  }
  return context;
};

export const ConstantsProvider = ({ children }) => {
  const { user } = useAuth();
  const [constants, setConstants] = useState({
    pipeline_stages: [],
    admin_pipeline_view_stages: [],
    all_pipeline_stages: [],
    industries: [],
    sectors: [],
    niches_by_sector: {},
    all_niches: [],
    activity_types: [],
    call_outcomes: [],
    decision_maker_status: [],
    closed_lost_reasons: [],
    script_types: [],
    country_markets: [],
    icp_fits: [],
    lead_sources: [],
    next_step_types: [],
    call_windows: [],
    reached_who_options: [],
    company_sizes: [],
    priorities: [],
    preferred_phones: [],
    best_times_to_call: [],
    setter_allowed_stages: [],
    setter_locked_stages: [],
    appointment_status_options: [],
    call_cadence_actions: {}
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchConstants();
      fetchUsers();
    }
  }, [user]);

  const fetchConstants = async () => {
    try {
      const response = await axios.get(`${API}/constants`);
      setConstants(response.data);
    } catch (error) {
      console.error('Error fetching constants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const getUserById = (id) => users.find(u => u.id === id);
  const getUserName = (id) => getUserById(id)?.name || 'Unassigned';
  
  // Get only setters for round robin
  const getSetters = () => users.filter(u => u.role === 'setter');
  
  // Get all assignable users
  const getAssignableUsers = () => users;

  // Get niches for a specific sector
  const getNichesForSector = (sector) => {
    return constants.niches_by_sector[sector] || [];
  };

  return (
    <ConstantsContext.Provider value={{ 
      ...constants, 
      users, 
      loading, 
      getUserById, 
      getUserName, 
      getSetters,
      getAssignableUsers,
      getNichesForSector,
      refetchUsers: fetchUsers,
      refetchConstants: fetchConstants
    }}>
      {children}
    </ConstantsContext.Provider>
  );
};
